import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import authService from '../services/authService';
import deviceService from '../services/device/deviceService';

// Create Auth Context
const AuthContext = createContext(null);

/**
 * Custom hook to use Auth Context
 * @throws {Error} If used outside of AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

/**
 * Auth Provider Component
 * Manages authentication state and provides auth methods to the app
 */
export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasSetHisPassword, setHasSetHisPassword] = useState(true);

  /**
   * Initialize authentication state on mount
   * Checks for existing access token, and if it is missing or expired,
   * tries to obtain a new one using the httpOnly refresh token cookie.
   * This is what keeps the user logged in across app restarts.
   */
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = authService.getAccessToken();

        if (token && !authService.isTokenExpired(token)) {
          // Valid token exists - user is authenticated
          setUser(authService.decodeToken(token));
          setIsAuthenticated(true);
          return;
        }

        // No token, or token expired -> try silent refresh via httpOnly cookie
        try {
          const newToken = await authService.refresh();
          setUser(authService.decodeToken(newToken));
          setIsAuthenticated(true);
        } catch (refreshError) {
          // No valid refresh cookie - user must log in again
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (error) {
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  /**
   * Login handler
   * @param {string} username - User username
   * @param {string} password - User password
   * @returns {Promise<Object>} Login response
   */
  const login = useCallback(async (username, password) => {
    try {
      // Native instalacija šalje stabilni deviceId u LoginDto. Na web-u
      // getDeviceIdForLogin vraća null, pa zahtev ostaje { username, password }.
      const deviceId = await deviceService.getDeviceIdForLogin();

      const response = await authService.login(
        username,
        password,
        deviceId ? { deviceId } : undefined
      );
      
      // Get user data from token
      const userData = authService.getCurrentUser();
      
      setUser(userData);
      setIsAuthenticated(true);
      
      // Track whether the user has set their password yet
      const hasSet = response?.data?.hasSetHisPassword !== false;
      setHasSetHisPassword(hasSet);
      
      return response;
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
      throw error;
    }
  }, []);

  /**
   * Set password handler
   * @param {string} newPassword - New password
   * @param {string} confirmNewPassword - Confirmation of new password
   * @returns {Promise<Object>} Set password response
   */
  const setPassword = useCallback(async (newPassword, confirmNewPassword) => {
    try {
      const response = await authService.setPassword(newPassword, confirmNewPassword);
      return response;
    } catch (error) {
      throw error;
    }
  }, []);

  /**
   * Logout handler
   * Clears tokens and resets auth state
   */
  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      setHasSetHisPassword(true);
    }
  }, []);

  /**
   * Refresh token handler
   * @returns {Promise<string>} New access token
   */
  const refresh = useCallback(async () => {
    try {
      const newToken = await authService.refresh();
      const userData = authService.decodeToken(newToken);
      
      setUser(userData);
      setIsAuthenticated(true);
      
      return newToken;
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
      throw error;
    }
  }, []);

  /**
   * Update user data manually (e.g., after profile update)
   */
  const updateUser = useCallback((userData) => {
    setUser(userData);
  }, []);

  const value = {
    isAuthenticated,
    user,
    loading,
    hasSetHisPassword,
    login,
    logout,
    refresh,
    updateUser,
    setPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
